import * as React from 'react'
import {
  
  
  
  
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import type {Column, ColumnDef, ColumnFiltersState, SortingState, Table as TanstackTable} from '@tanstack/react-table';
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

interface DataTableProps<TData, TValue> {
  columns: Array<ColumnDef<TData, TValue>>
  data: Array<TData>
  emptyState?: React.ReactNode
  onTableReady?: (table: TanstackTable<TData>) => void
  getRowId?: (row: TData, index: number) => string
  initialSorting?: SortingState
  globalFilter?: string
  onGlobalFilterChange?: (value: string) => void
  globalFilterFn?: 'auto' | ((row: any, columnId: string, value: any) => boolean)
  columnFilters?: ColumnFiltersState
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void
  className?: string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  emptyState,
  onTableReady,
  getRowId,
  initialSorting,
  globalFilter,
  onGlobalFilterChange,
  globalFilterFn = 'auto',
  columnFilters,
  onColumnFiltersChange,
  className,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting ?? [])
  const [internalColumnFilters, setInternalColumnFilters] = React.useState<ColumnFiltersState>([])

  const activeColumnFilters = columnFilters ?? internalColumnFilters

  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: {
      sorting,
      globalFilter,
      columnFilters: activeColumnFilters,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange,
    onColumnFiltersChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(activeColumnFilters) : updater
      if (onColumnFiltersChange) onColumnFiltersChange(next)
      else setInternalColumnFilters(next)
    },
    globalFilterFn: globalFilterFn === 'auto' ? undefined : globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  React.useEffect(() => {
    onTableReady?.(table)
  }, [table, onTableReady])

  return (
    <div className={cn('rounded-md border bg-card', className)}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() }}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-32 text-center">
                {emptyState ?? (
                  <span className="text-sm text-muted-foreground">No results.</span>
                )}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>
  title: string
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  if (!column.getCanSort()) {
    return <div className={cn('text-xs font-medium', className)}>{title}</div>
  }

  const sorted = column.getIsSorted()

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => column.toggleSorting(sorted === 'asc')}
      className={cn('-ml-2 h-7 px-2 text-xs font-medium', className)}
    >
      {title}
      {sorted === 'asc' ? (
        <ArrowUp className="ml-1 size-3" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="ml-1 size-3" />
      ) : (
        <ChevronsUpDown className="ml-1 size-3 opacity-50" />
      )}
    </Button>
  )
}
